/**
 * Lesson 6: How Prices Move
 *
 * Explains price discovery, order flow, and supply/demand dynamics.
 */

import type { Lesson, SimulationScenario } from '../manager'
import type { Market, OrderBook, Order, Trade } from '../../engine/types'

// Helper to create an order
function createOrder(
  id: string,
  side: 'buy' | 'sell',
  price: number,
  quantity: number,
  timestamp: number
): Order {
  return {
    id,
    side,
    type: 'limit',
    price,
    quantity,
    timestamp,
    status: 'open',
    filledQuantity: 0,
  }
}

// Helper to create a trade
function createTrade(
  id: string,
  price: number,
  quantity: number,
  timestamp: number
): Trade {
  return {
    id,
    price,
    quantity,
    timestamp,
    buyOrderId: 'buy-' + id,
    sellOrderId: 'sell-' + id,
    takerSide: 'buy',
  }
}

// Order book with trade history to show price movement
// Matches the example in Page 2: Best Bid $0.60, Best Ask $0.62
const now = Date.now()
const priceDiscoveryOrderBook: OrderBook = {
  bids: [
    createOrder('bid-1', 'buy', 0.60, 80, now - 5000),
    createOrder('bid-2', 'buy', 0.58, 100, now - 4000),
    createOrder('bid-3', 'buy', 0.55, 120, now - 3000),
    createOrder('bid-4', 'buy', 0.52, 60, now - 2000),
  ],
  asks: [
    createOrder('ask-1', 'sell', 0.62, 100, now - 5000),  // 100 shares at $0.62
    createOrder('ask-2', 'sell', 0.65, 50, now - 4000),   // 50 shares at $0.65
    createOrder('ask-3', 'sell', 0.68, 90, now - 3000),
    createOrder('ask-4', 'sell', 0.72, 40, now - 2000),
  ],
  trades: [
    // Trade history showing price movement (upward trend)
    createTrade('t1', 0.45, 20, now - 300000),
    createTrade('t2', 0.48, 30, now - 240000),
    createTrade('t3', 0.52, 25, now - 180000),
    createTrade('t4', 0.55, 40, now - 120000),
    createTrade('t5', 0.58, 35, now - 60000),
    createTrade('t6', 0.61, 50, now - 30000),
  ],
  lastPrice: 0.61,
}

const priceDiscoveryMarket: Market = {
  id: 'lesson-6-market',
  question: 'Will the startup reach profitability this year?',
  description: 'Watch how prices move with supply and demand',
  outcomes: ['Yes', 'No'] as const,
  orderBook: priceDiscoveryOrderBook,
  resolved: false,
  resolution: null,
}

export const priceDiscoveryScenario: SimulationScenario = {
  id: 'price-discovery',
  name: 'Price Discovery',
  description: 'Observe how new information affects market prices',
  market: priceDiscoveryMarket,
  initialOrderBook: priceDiscoveryOrderBook,
  userStartingBalance: 100,
}

export const lesson6: Lesson = {
  id: 'lesson-6',
  title: 'How Prices Move',
  description: 'Understand order flow, supply and demand, and price discovery',
  order: 6,
  prerequisites: ['lesson-5'],
  completionCriteria: { type: 'all-content' },
  objectives: [
    'Understand what moves prices in a market',
    'See how buying pressure raises prices',
    'Learn how new information affects markets',
  ],
  content: [
    // Page 1: What Moves Prices?
    {
      type: 'text',
      data: {
        markdown: `# How Prices Move

You've learned how to trade. Now let's understand **why prices change**.

## The Simple Answer: Supply and Demand

- **Stronger aggressive buying pressure** → Price goes **up** 📈
- **Stronger aggressive selling pressure** → Price goes **down** 📉

But let's look deeper at the mechanics.

## Order Flow

**Order flow** is the stream of buy and sell orders coming into the market.

| Order Flow | Effect | Why |
|------------|--------|-----|
| Aggressive buying | Price rises | Buyers consume asks, pushing price up |
| Aggressive selling | Price falls | Sellers consume bids, pushing price down |
| Balanced flow | Price stable | Aggressive buyers and sellers cancel out |

> **Think of it like**: Each buy order "eats" the lowest ask. Each sell order "eats" the highest bid.`,
      },
    },

    // Page 2: Buying Pressure
    {
      type: 'text',
      data: {
        markdown: `## Buying Pressure Example

Let's say the order book looks like this:
- Best Bid: $0.60
- Best Ask: $0.62 (100 shares available)
- Next Ask: $0.65 (50 shares available)

### Scenario: Large Market Buy Order (150 shares)

1. First, 100 shares fill at $0.62 (exhausting that level)
2. Then, 50 shares fill at $0.65

**Result:**
- Last price moves from $0.62 → **$0.65**
- Best ask is now $0.65 or higher
- Price went **up** due to buying pressure!

### The Key Insight

Aggressive buying:
1. Takes liquidity from the ask side
2. Moves the price upward
3. The larger the order, the more it moves price

This is why you saw **slippage** in market orders—your own buying pushes the price against you.`,
        visualizationId: 'order-book',
      },
    },

    // Page 3: Information and Price Discovery
    {
      type: 'text',
      data: {
        markdown: `## Information and Price Discovery

**Price discovery** is how markets find the "correct" price through trading.

### How New Information Moves Prices

1. **News breaks** (e.g., "Company announces better-than-expected earnings")
2. **Traders reassess** the probability
3. **They trade** based on their new beliefs
4. **Price adjusts** to reflect the new information

### Example: Startup Funding News

Before news: Market at $0.45 (45% chance of profitability)

📰 **Breaking: Startup raises $100M in funding**

Traders think: "With more money, they're more likely to succeed!"
- Buyers rush in
- Price jumps to $0.61

The market has **discovered** a new probability based on new information.

> **This is why prediction markets are powerful**: Prices update in real-time as new information becomes available.`,
        visualizationId: 'price-history',
      },
    },

    // Page 4: Watching Price History
    {
      type: 'text',
      data: {
        markdown: `## Reading Price History

Look at the **Price History** chart. It shows how the price has changed over time.

### What to Look For

**Trending Up** 📈
- Series of higher prices
- Increasing bullishness
- More aggressive buying than selling

**Trending Down** 📉
- Series of lower prices
- Increasing bearishness
- More aggressive selling than buying

**Sideways/Choppy** ↔️
- Price bouncing around a range
- Uncertainty or balance
- Aggressive buyers and sellers roughly matched

### Volume Matters

Bigger moves with **more shares traded** matter more—volume is a confidence signal.

> **Tip**: In our example market, notice how the price has been rising. What might have caused this?`,
        visualizationId: 'price-history',
      },
    },

    // Page 5: Interactive observation
    {
      type: 'interactive',
      data: {
        scenario: priceDiscoveryScenario,
        instructions: `## Observe Price Discovery

Look at the market and think about what you see:

1. **Check the Price History** - The price has moved from about $0.45 to $0.61 over time. What might have caused this rise?

2. **Look at the Order Book** - Notice how the bids and asks are positioned. The best bid is $0.60 and best ask is $0.62—exactly as shown in the example!

3. **Consider the Question** - "Will the startup reach profitability this year?" The rising price suggests traders are becoming more confident.

### Reflection Questions
- If you believed profitability was only 50% likely, what would you do?
- What news might cause the price to drop suddenly?

When you've observed and thought about these questions, click "Mark as Complete."`,
        hints: [
          'The price history shows an upward trend—more buying than selling.',
          'Current price around $0.61 means the market thinks there\'s about a 61% chance of profitability.',
          'If you disagree with the market, you could trade against it!',
        ],
        successCondition: () => true, // Manual completion for observation
      },
    },

    // Quiz
    {
      type: 'quiz',
      data: {
        question: 'If many traders suddenly start buying Yes shares, what happens to the price?',
        options: [
          { id: 'a', text: 'Price stays the same' },
          { id: 'b', text: 'Price goes down' },
          { id: 'c', text: 'Price goes up' },
          { id: 'd', text: 'The market closes' },
        ],
        correctOptionId: 'c',
        explanation:
          'When many traders buy Yes shares, they consume the available asks, pushing the price upward. It is the aggressive buying—traders willing to pay current asking prices—that moves the price up. The reverse happens with aggressive selling pressure.',
      },
    },
  ],
}
